"use client"

import { CheckCircleIcon } from "@heroicons/react/20/solid"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"
import Spinner from "../Spinner"

const STATE_DURATION = 1800

type StatusFooterProps = {
  states: readonly string[]
  isPlaying: boolean
}

const StatusFooter = ({ states, isPlaying }: StatusFooterProps) => {
  const [stateIndex, setStateIndex] = useState(0)
  const isComplete = stateIndex === states.length - 1

  useEffect(() => {
    if (!isPlaying) {
      return
    }

    const timeouts = states.slice(0, -1).map((_, index) =>
      setTimeout(
        () => {
          setStateIndex(index + 1)
        },
        STATE_DURATION * (index + 1)
      )
    )

    return () => {
      for (const timeout of timeouts) {
        clearTimeout(timeout)
      }
    }
  }, [isPlaying, states])

  return (
    <div className="flex items-center gap-3 overflow-hidden px-5 py-4">
      <div className="flex size-5 shrink-0 items-center justify-center">
        <AnimatePresence mode="popLayout">
          {isComplete ? (
            <motion.div
              key="check"
              initial={{ opacity: 0, scale: 0.7, filter: "blur(4px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.3 }}
            >
              <CheckCircleIcon className="size-5.5 text-green-500" />
            </motion.div>
          ) : (
            <motion.div
              key="spinner"
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 0.5, filter: "blur(4px)" }}
              transition={{ duration: 0.3 }}
            >
              <Spinner className="text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence mode="popLayout">
        <motion.p
          key={stateIndex}
          initial={{ y: 5, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -5, opacity: 0 }}
          transition={{
            y: { type: "spring", stiffness: 750, damping: 30 },
            opacity: { duration: 0.15 },
          }}
          className="whitespace-nowrap font-semibold text-base/5 text-white tracking-tight"
        >
          {states[stateIndex]}
        </motion.p>
      </AnimatePresence>
    </div>
  )
}

export default StatusFooter
